import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FlaskConical, Save, ShieldCheck } from "lucide-react";

const CSV = (arr) => (arr || []).join(", ");
const toArr = (str) => (str || "").split(",").map((s) => s.trim()).filter(Boolean);

export default function AdminLabs() {
  const [labs, setLabs] = useState([]);
  const [sid, setSid] = useState("");
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/labs");
    setLabs(data);
    if (data.length && !sid) setSid(data[0].id);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const l = labs.find((x) => x.id === sid);
    if (!l) return;
    setForm({
      name: l.name || "", address: l.address || "", phone: l.phone || "",
      rating: l.rating || 4.5, tests: CSV(l.tests), home_collection: String(!!l.home_collection),
      equipment: CSV(l.equipment),
      catalogue_json: JSON.stringify(l.test_catalogue || [], null, 2),
    });
  }, [sid, labs]);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    setSaving(true);
    try {
      let catalogue;
      try { catalogue = JSON.parse(form.catalogue_json || "[]"); }
      catch { toast.error("Test catalogue JSON is invalid"); setSaving(false); return; }
      await api.patch(`/admin/labs/${sid}`, {
        name: form.name, address: form.address, phone: form.phone,
        rating: Number(form.rating), tests: toArr(form.tests),
        home_collection: form.home_collection === "true",
        equipment: toArr(form.equipment),
        test_catalogue: catalogue,
      });
      toast.success("Lab updated"); load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 pb-24 sm:pb-8">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-emerald-500/15 text-emerald-700 flex items-center justify-center"><FlaskConical className="w-5 h-5" /></div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Admin · Labs</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Manage lab details</h1>
        </div>
      </div>

      <Card className="p-4 rounded-2xl mb-4">
        <Label>Choose lab</Label>
        <Select value={sid} onValueChange={setSid}>
          <SelectTrigger data-testid="admin-lab-select"><SelectValue /></SelectTrigger>
          <SelectContent>{labs.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
        </Select>
      </Card>

      <Card className="p-5 rounded-2xl space-y-3">
        <div><Label>Name</Label><Input data-testid="l-name" value={form.name || ""} onChange={set("name")} /></div>
        <div><Label>Address</Label><Input data-testid="l-address" value={form.address || ""} onChange={set("address")} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Phone</Label><Input data-testid="l-phone" value={form.phone || ""} onChange={set("phone")} /></div>
          <div><Label>Rating</Label><Input data-testid="l-rating" type="number" step="0.1" value={form.rating || ""} onChange={set("rating")} /></div>
        </div>
        <div>
          <Label>Tests offered (comma separated)</Label>
          <Textarea data-testid="l-tests" rows={3} value={form.tests || ""} onChange={set("tests")} placeholder="CBC, Lipid Profile, Thyroid, HbA1c, Vitamin D…" />
        </div>
        <div>
          <Label>Equipment on-site (comma separated)</Label>
          <Textarea data-testid="l-equipment" rows={2} value={form.equipment || ""} onChange={set("equipment")} placeholder="MRI 1.5T, CT Scan, Ultrasound, Digital X-Ray, ECG…" />
        </div>
        <div>
          <Label>Test catalogue with pricing (JSON)</Label>
          <p className="text-xs text-slate-500 mb-1">Each entry: {'{ name, category, price }'} — shown to patients on the lab page.</p>
          <Textarea data-testid="l-catalogue" rows={7} className="font-mono text-xs" value={form.catalogue_json || "[]"} onChange={set("catalogue_json")} />
        </div>
        <div>
          <Label>Home collection</Label>
          <Select value={form.home_collection || "false"} onValueChange={set("home_collection")}>
            <SelectTrigger data-testid="l-home"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="true">Available</SelectItem><SelectItem value="false">Centre visit only</SelectItem></SelectContent>
          </Select>
        </div>
      </Card>

      <div className="mt-6 flex items-center justify-end gap-3">
        <div className="text-xs text-slate-500 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Admin-only actions</div>
        <Button data-testid="admin-save-lab" onClick={save} disabled={saving || !sid} className="bg-emerald-600 hover:bg-emerald-500 text-white">
          <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
